import { ROUTES } from '@/constants/routes';
import { Stack, Typography } from '@mui/material';
import { SVLink, SVSummaryCard } from '../core';
import { formatDateTimeFromUTC } from '@/utils/date';
import { SVSummaryCardItem, Status } from '@/types/common';
import { TeammateDetails } from '@/types/teammates';
import { getTeammateRoleDisplayName } from '@/utils/teammates';
import StatusLabel from '../StatusLabel';
import { FORM_FIELDS } from '@/constants/formFields';
import { createFullName } from '@/utils/string';
import { formatPhoneNumber } from '@/utils/phoneNumber';
import { titleCase } from '@/utils/string';

type TeammateBasicInfoProps = {
  teammateDetails: TeammateDetails;
};

export default function TeammateBasicInfo({
  teammateDetails,
}: TeammateBasicInfoProps) {
  const {
    user,
    company,
    role,
    status,
    createdAt,
    advisorUrl,
    members,
    deactivatedByUser,
  } = teammateDetails;

  const supportMemberNames = members.map(member =>
    createFullName(member.user.firstName, member.user.lastName),
  );
  const deactivatedByUserFullName = deactivatedByUser
    ? createFullName(deactivatedByUser.firstName, deactivatedByUser.lastName)
    : null;

  const {
    firstName,
    lastName,
    email,
    title,
    cellPhoneNumber,
    businessPhoneNumber,
    bio,
    avatar,
    lastLogin,
  } = user;

  const baseItems: SVSummaryCardItem[] = [
    {
      label: FORM_FIELDS.FIRST_NAME.label,
      text: firstName,
    },
    {
      label: FORM_FIELDS.LAST_NAME.label,
      text: lastName,
    },
    {
      label: FORM_FIELDS.EMAIL.label,
      text: email,
    },
    {
      label: FORM_FIELDS.COMPANY.label,
      customContent: company ? (
        <SVLink to={`${ROUTES.COMPANIES}/${company.uuid}`}>
          {company.name}
        </SVLink>
      ) : null,
    },
    {
      label: 'Role',
      text: getTeammateRoleDisplayName(role),
    },
    {
      label: 'Title',
      text: titleCase(title.replace('_', ' ')),
    },
    {
      label: 'Support Members',
      customContent: supportMemberNames.length ? (
        <Stack spacing={1}>
          {supportMemberNames.map((member: string) => (
            <Typography key={member}>{member}</Typography>
          ))}
        </Stack>
      ) : null,
    },
    {
      label: 'Phone for Auth',
      text: formatPhoneNumber(cellPhoneNumber),
    },
    {
      label: 'Business Phone',
      text: formatPhoneNumber(businessPhoneNumber),
    },
    {
      label: 'Bio',
      text: bio || 'N/A',
    },
    {
      label: 'Advisor URL',
      text: advisorUrl || 'N/A',
      customContent: advisorUrl ? (
        <SVLink target="_blank" to={advisorUrl}>
          {advisorUrl}
        </SVLink>
      ) : null,
    },
    {
      label: 'Status',
      customContent: <StatusLabel status={status} />,
    },
    {
      label: 'Created Date',
      text: formatDateTimeFromUTC(createdAt),
    },
    {
      label: 'Last Login Date',
      text: lastLogin ? formatDateTimeFromUTC(lastLogin) : 'N/A',
    },
  ];

  const deactivationItems: SVSummaryCardItem[] =
    status === Status.INACTIVE
      ? [
          {
            label: 'Teammate Deactivated At',
            text: deactivatedByUser
              ? formatDateTimeFromUTC(user.deactivatedDate, true)
              : 'N/A',
          },
          {
            label: 'Deactivated By',
            customContent: deactivatedByUser ? (
              <Stack spacing={1}>
                <Typography>{deactivatedByUserFullName}</Typography>
                <Typography>{deactivatedByUser.email}</Typography>
              </Stack>
            ) : null,
          },
        ]
      : [];

  const items: SVSummaryCardItem[] = [...baseItems, ...deactivationItems];

  return <SVSummaryCard label="Basic Info" items={items} avatar={avatar} />;
}
